/**
 * useAttentionStream — GM Attention Phase 1 (2026-07-17).
 *
 * Opens the same-origin WS to /api/attention/stream, sends the in-band JWT auth
 * frame FIRST (wsAuthFrame(), same contract as the ttyd proxy / voice WS), and
 * feeds every {type:"roster",...} message into attentionStore. Reconnects with
 * a fixed backoff on close. Mounted once at App level.
 */
import { useEffect, useRef } from 'react';

import { wsAuthFrame } from '../services/api';
import { useAttentionStore } from '../stores/attentionStore';

const RECONNECT_MS = 3000;

export function useAttentionStream(): void {
  const setRoster = useAttentionStore((s) => s.setRoster);
  const setConnected = useAttentionStore((s) => s.setConnected);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closed = useRef(false);

  useEffect(() => {
    closed.current = false;

    const connect = (): WebSocket | null => {
      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${scheme}//${window.location.host}/api/attention/stream`;
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        // Construction can throw on a malformed URL / blocked scheme; schedule a
        // retry rather than leaving the dock permanently disconnected.
        console.error('[attention] WS construct failed:', (e as Error).message);
        scheduleReconnect();
        return null;
      }

      ws.onopen = () => {
        const frame = wsAuthFrame();
        if (frame) ws.send(JSON.stringify(frame));
        setConnected(true);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'roster') {
            // Phase 2: adapters (bridge health) rides every roster frame —
            // dropping it here was the live-gate bug that hid the chips.
            setRoster({
              sessions: msg.sessions ?? [],
              approvals: msg.approvals ?? [],
              adapters: msg.adapters ?? {},
            });
          }
        } catch (e) {
          console.error('[attention] bad frame:', (e as Error).message);
        }
      };
      ws.onclose = () => {
        setConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => {
        // onclose fires after onerror; reconnect is scheduled there. Log so an
        // infra failure is visible (Rule #19 — no silent swallow).
        console.error('[attention] WS error');
      };
      return ws;
    };

    let current: WebSocket | null = connect();

    function scheduleReconnect(): void {
      if (closed.current) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (!closed.current) current = connect();
      }, RECONNECT_MS);
    }

    return () => {
      closed.current = true;
      if (timer.current) clearTimeout(timer.current);
      if (current) {
        current.onclose = null; // prevent reconnect-on-unmount
        current.close();
      }
      setConnected(false);
    };
  }, [setRoster, setConnected]);
}
